// The remediation console's rules, as pure functions.
//
// `screens/admin-remediations.tsx` only renders. What is decided here —
// whether Apply may fire, what the operator is being told, which entities are
// even candidates — is the logic that must be testable without a DOM, so it
// lives in pure functions with their own tests.
//
// The server owns the safety. Every rule below is a *second* copy of a
// judgement the API already made and will make again on apply; none of it is
// load-bearing. A UI that let a click through would still hit a preflight that
// refuses, a confirmation compared against the server's own spelling, and an
// `UPDATE` whose `WHERE` carries the preflight's predicates. This layer exists
// so the operator finds out before they type, not so the write is safe.

import type {
  RemediationAction,
  RemediationCandidate,
  RemediationPreview,
  RemediationVerdict,
} from '@/api/manage-types';

/** What each repair is called and does, in the operator's words. */
export const ACTION_COPY: Record<RemediationAction, { label: string; blurb: string }> = {
  retry_retired_ad_pause: { label: 'Retry retired ad pause', blurb: 'Retries a blocked pause-only recovery while retaining its incident and receipts.' },
  retry_binding_repair: { label: 'Retry creative binding recovery', blurb: 'Restarts an exhausted recovery while preserving replacement receipts and the requested campaign state.' },
  reset_retry_budget: {
    label: 'Reset the retry budget',
    blurb:
      'Returns a stranded entity to the retry sweeper. Nothing else re-drives a row '
      + 'that has spent all ten attempts.',
  },
  force_audience_refresh: {
    label: 'Force an audience refresh',
    blurb:
      'Makes a refresh due immediately, ignoring the five-minute debounce that '
      + 'otherwise accepts the request and does nothing.',
  },
  release_ttd_drop_intent: {
    label: 'Release a stuck TTD drop intent',
    blurb:
      'Clears a drop-create intent that provably never dispatched, so the '
      + 'materializer can mint a fresh one.',
  },
  remint_meta_container: {
    label: 'Re-mint a Meta audience',
    blurb:
      'Replaces one audience\'s Meta custom audience with a brand-new one, declared '
      + 'USER_PROVIDED_ONLY; the SAM audience keeps its id, so campaigns keep their references. '
      + 'Offered only while no campaign past draft and no ad set uses the old one. Nothing is deleted at '
      + 'Meta: delete the old id in Ads Manager once its replacement is synced. An existing '
      + 'audience\'s declaration is never edited.',
  },
  repin_failed_publish: {
    label: 'Re-pin a failed Meta publish',
    blurb:
      'Moves a failed, never-published Meta campaign\'s creatives to each creative\'s newest '
      + 'approved revision and queues the publish again. The campaign keeps its id, and the Meta '
      + 'campaign and ad sets already created are reused. Offered only while no ad exists for the '
      + 'campaign\'s current revision and each creative\'s newest approved revision is previewed '
      + 'and synced to Meta — fix the creative first (for example a Book Now call to action), '
      + 'then re-pin.',
  },
};

/**
 * Every repair, once. The route's `searchEnum` and the screen's buttons read
 * this; `tsc` proves it names the whole union, so a new server action cannot
 * reach the console without a button.
 */
export const REMEDIATION_ACTIONS = [
  'reset_retry_budget',
  'retry_binding_repair',
  'retry_retired_ad_pause',
  'force_audience_refresh',
  'release_ttd_drop_intent',
  'remint_meta_container',
  'repin_failed_publish',
] as const satisfies readonly RemediationAction[];

type UnlistedRemediationAction = Exclude<RemediationAction, (typeof REMEDIATION_ACTIONS)[number]>;

/** Fails `tsc` the moment the generated union carries an action this list omits. */
export const REMEDIATION_ACTIONS_ARE_EXHAUSTIVE: [UnlistedRemediationAction] extends [never]
  ? true
  : never = true;

/**
 * What each refusal means.
 *
 * A verdict the UI cannot name would render as a blank reason, which is the
 * failure this whole vocabulary exists to avoid — the server sends a sentence
 * too, but a missing label here means the operator sees a code.
 */
export const VERDICT_COPY: Record<RemediationVerdict, string> = {
  ready: 'Ready',
  refuse_not_found: 'No such row',
  refuse_status: 'Wrong state',
  refuse_nothing_to_do: 'Nothing to do',
  refuse_provider_evidence: 'Provider evidence',
  refuse_lease_held: 'Another run holds it',
  refuse_writes_disabled: 'Writes are switched off',
  refuse_active_operation: 'Work in flight',
  refuse_archived: 'Archived',
  refuse_nonce: 'Nonce does not match',
};

export function verdictLabel(verdict: RemediationVerdict): string {
  return VERDICT_COPY[verdict] ?? verdict;
}

export function isReady(preview: RemediationPreview | undefined): boolean {
  return preview?.verdict === 'ready';
}

/**
 * May Apply fire?
 *
 * The typed string must equal the server's spelling **exactly** — not trimmed,
 * not case-folded. Every existing operator script demands the same, and the
 * point of an exact match is that it cannot be produced by muscle memory or by
 * pasting the previous entity's confirmation.
 *
 * Compared against `preview.confirmation` rather than a string this module
 * builds: if the two ever disagreed, a locally-computed one would silently
 * disable the only gate a click cannot satisfy.
 */
export function canApply(
  preview: RemediationPreview | undefined,
  typed: string,
  pending = false,
): boolean {
  if (!preview || pending) return false;
  if (!isReady(preview)) return false;
  return typed === preview.confirmation;
}

/**
 * How close the operator is, for the field's own hint.
 *
 * `mismatch` only once they have typed something — an empty field is not an
 * error, it is the starting state.
 */
export function confirmationState(
  preview: RemediationPreview | undefined,
  typed: string,
): 'idle' | 'match' | 'mismatch' {
  if (!preview || typed.length === 0) return 'idle';
  return typed === preview.confirmation ? 'match' : 'mismatch';
}

/** The candidates for one action, in the order the server found them. */
export function candidatesFor(
  candidates: readonly RemediationCandidate[] | undefined,
  action: RemediationAction,
): RemediationCandidate[] {
  return (candidates ?? []).filter((candidate) => candidate.action === action);
}

/** A stable key for a candidate row, since an entity can appear per target. */
export function candidateKey(candidate: RemediationCandidate): string {
  const { entity_kind, entity_id, target, operation_id } = candidate.target;
  return `${entity_kind}:${entity_id}:${target ?? '-'}${operation_id ? `:${operation_id}` : ''}`;
}

/**
 * Did every statement land?
 *
 * `release_ttd_drop_intent` writes two journal copies that are not atomic
 * together, so "applied" is a per-step fact. A screen that read only the first
 * step, or that showed one tick for the whole run, would report success over a
 * half-applied repair — which is worse than the script it replaces, because
 * the script at least prints both.
 */
export function applyOutcome(
  steps: readonly { name: string; rows_affected: number; applied: boolean }[] | undefined,
): 'applied' | 'partial' | 'no_change' {
  if (!steps || steps.length === 0) return 'no_change';
  if (steps.every((step) => step.applied)) return 'applied';
  if (steps.some((step) => step.applied)) return 'partial';
  return 'no_change';
}

/**
 * What to tell the operator when nothing changed.
 *
 * Zero rows is not a failure and not a success: it means the row moved between
 * the preview and the apply, and the statement correctly declined to overwrite
 * it. Saying "failed" would send someone looking for a bug in the repair.
 */
export function outcomeMessage(outcome: ReturnType<typeof applyOutcome>): string {
  switch (outcome) {
    case 'applied':
      return 'Applied.';
    case 'partial':
      return 'Partly applied — one statement matched and another did not. Re-run the '
        + 'preview; the repair is safe to run again.';
    case 'no_change':
      return 'Nothing changed. The row moved between the preview and the apply, so the '
        + 'repair declined to overwrite it. Re-run the preview to see where it is now.';
  }
}

/**
 * The re-mint step that clears `audiences.meta_audience_id`, named after its
 * table like every other remediation step. It runs last in the batch and only
 * when every eligibility clause still holds, so it alone says the re-mint
 * landed.
 */
export const REMINT_GUARDED_STEP = 'audiences';

/**
 * The re-pin step that moves the `campaign_creatives` pins. The batch runs
 * `audit_events`, `sync_state`, then this step, last, so the pins move only
 * when every eligibility clause still held; zero rows here is the lost race.
 * The queue send that follows (`queue: …`) is a separate step, and failing it
 * leaves a reset `pending` row the retry sweeper re-drives.
 */
export const REPIN_GUARDED_STEP = 'campaign_creatives';

/**
 * The outcome line for one action's apply.
 *
 * A re-mint changes nothing at Meta on apply: it retires the old id in D1 and
 * queues the sync that mints the new container. A bare "Applied." would read
 * as "done", and leave the old Meta audience behind with nobody told to delete
 * it. Every other action keeps `outcomeMessage`.
 *
 * A re-mint is decided on its guarded `audiences` step alone, not on every
 * step: the apply is one D1 batch, so it cannot be half-applied, and its
 * journal resets can legitimately match no row (a Meta leg with no revision
 * row yet). Reading those as "Partly applied" would send the operator to
 * preview again after the id was already cleared, and never tell them to
 * delete the old one.
 *
 * A re-pin is decided the same way, on `REPIN_GUARDED_STEP`: once the pins
 * moved, the publish runs with or without the queue send, because the reset
 * `pending` row is the retry sweeper's to re-drive.
 */
export function outcomeMessageFor(
  action: RemediationAction,
  steps: Parameters<typeof applyOutcome>[0],
): string {
  if (
    action === 'repin_failed_publish'
    && steps?.some((step) => step.name === REPIN_GUARDED_STEP && step.applied)
  ) {
    const queue = steps.find((step) => step.name.startsWith('queue:'));
    const queued = queue
      ? queue.applied
        ? 'The publish is queued'
        : 'The publish could not be queued; the retry sweeper picks the reset campaign up on its next pass'
      : 'The retry sweeper picks the reset campaign up on its next pass';
    return `Applied. The campaign now pins each creative's newest approved revision. ${queued}, `
      + 'reusing the Meta campaign and ad sets already created and creating the ads. The old '
      + 'revision\'s Meta creative stays in the ad account, attached to nothing.';
  }
  if (
    action === 'remint_meta_container'
    && steps?.some((step) => step.name === REMINT_GUARDED_STEP && step.applied)
  ) {
    return 'Applied. The old Meta audience id is retired here and a sync is queued; it creates a '
      + 'new USER_PROVIDED_ONLY audience and uploads it. Once the new one is synced, delete the '
      + 'old id by hand in Meta Ads Manager.';
  }
  return outcomeMessage(applyOutcome(steps));
}
